import { useEffect, useState, useCallback } from 'react';
import { Plus, X, AlertTriangle, RotateCw, Pause, Trash2, Package, Monitor } from 'lucide-react';
import { api } from '../../../api/client';
import type { TenantLicense, LicensePackage, LicenseModule, ModuleCode } from '../../../types/domain';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import { Uputstvo, Pojam } from '../../../components/Uputstvo';

const STATUS_LABELS: Record<string, { color: string; label: string }> = {
  active: { color: '#10b981', label: 'Aktivna' },
  replaced: { color: '#6b7280', label: 'Zamenjena' },
  expired: { color: '#fca5a5', label: 'Istekla' },
  suspended: { color: '#f59e0b', label: 'Suspendovana' },
};

function daysBetween(a: string, b: string): number {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / (24 * 60 * 60 * 1000));
}

export function LicensesTab({ tenantId }: { tenantId: string }) {
  const [licenses, setLicenses] = useState<TenantLicense[]>([]);
  const [packages, setPackages] = useState<LicensePackage[]>([]);
  const [modules, setModules] = useState<LicenseModule[]>([]);
  const [showHistorical, setShowHistorical] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renewing, setRenewing] = useState<TenantLicense | null>(null);
  const [creatingFromPackage, setCreatingFromPackage] = useState(false);
  const [creatingIndividual, setCreatingIndividual] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState<TenantLicense | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TenantLicense | null>(null);
  /** Koliko je mesta zauzeto sada - bez toga „mesta: 1" ne odgovara na pitanje zašto klijenta izbacuje sa kase. */
  const [mesta, setMesta] = useState<{ seats: number | null; used: number } | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [l, p, m, s] = await Promise.all([
        api.get<TenantLicense[]>(`/admin/tenants/${tenantId}/licenses`),
        api.get<LicensePackage[]>('/admin/license-packages'),
        api.get<LicenseModule[]>('/admin/license-packages/modules'),
        api.get<{ seats: number | null; used: number }>(`/admin/tenants/${tenantId}/licenses/seats`),
      ]);
      setLicenses(l);
      setPackages(p);
      setModules(m);
      setMesta(s);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    }
  }, [tenantId]);

  useEffect(() => { refresh(); }, [refresh]);

  const confirmSuspend = async () => {
    if (!suspendTarget) return;
    await api.post(`/admin/tenants/${tenantId}/licenses/${suspendTarget.id}/suspend`, {});
    refresh();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await api.del(`/admin/tenants/${tenantId}/licenses/${deleteTarget.id}`);
    refresh();
  };

  const filtered = showHistorical ? licenses : licenses.filter((l) => l.status === 'active');

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 14, color: 'var(--color-text-strong)', fontWeight: 600 }}>Licence ({filtered.length})</h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-muted)' }}>
          <input type="checkbox" checked={showHistorical} onChange={(e) => setShowHistorical(e.target.checked)} />
          Prikaži i istorijske
        </label>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreatingIndividual(true)} style={btnSecondary}><Plus size={13} /> Pojedinačna</button>
        <button onClick={() => setCreatingFromPackage(true)} style={btnPrimary}><Package size={14} /> Iz paketa</button>
      </div>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}

      <Uputstvo id="licence-klijenta" naslov="Kako se čita ova tabela">
        <Pojam naziv="Jedan red = jedan modul">
          Klijent ima po jednu licencu za svaki modul koji koristi. Dodela paketa napravi ih više odjednom.
        </Pojam>
        <Pojam naziv="Mesta">
          Koliko <strong>uređaja</strong> sme istovremeno da bude prijavljeno. Kad se prijavi još jedan,
          najstarija kasa se odjavljuje u roku od pet minuta. Mesta preko prvog se naplaćuju.
        </Pojam>
        <Pojam naziv="Dana">
          Koliko je ostalo do isteka. Posle isteka ide <strong>7 dana grejsa</strong> (sve radi), a zatim
          klijent <strong>vidi podatke ali ne izdaje dokumente</strong>.
        </Pojam>
        <Pojam naziv="Zamenjena">
          Obnova ne menja stari red nego pravi nov. Stari ostaje kao <code>replaced</code> da bi se posle
          znalo po čemu je koji račun izdat - zato ih ima više za isti modul. Merodavan je onaj koji važi
          <strong> najduže</strong>.
        </Pojam>
        <div style={{ padding: '8px 10px', borderRadius: 6, background: 'var(--color-bg)', lineHeight: 1.6 }}>
          <strong>Suspenzija licence nije suspenzija naloga.</strong> Ovde se gasi jedan modul; nalog se
          suspenduje na kartici klijenta i tada ne radi ništa osim prijave, podrške i naplate.
        </div>
      </Uputstvo>

      {mesta && mesta.seats != null && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
          padding: '8px 12px', borderRadius: 6, fontSize: 12,
          background: mesta.used > mesta.seats ? 'rgba(245,158,11,0.15)' : 'var(--color-bg-surface)',
          color: mesta.used > mesta.seats ? '#fcd34d' : 'var(--color-text-muted)',
          border: '1px solid var(--color-border)',
        }} data-testid="mesta-zauzeto">
          <Monitor size={13} />
          Prijavljenih uređaja sada: <strong>{mesta.used}</strong> od <strong>{mesta.seats}</strong> plaćenih mesta.
          {mesta.used > mesta.seats && ' Sledeća prijava izbacuje najstariju kasu.'}
        </div>
      )}

      <div style={card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%' }}>
            <thead><tr style={{ background: 'var(--color-bg)' }}>
              <th style={th}>Modul</th><th style={th}>Status</th>
              <th style={{ ...th, textAlign: 'right' }} title="Koliko uređaja sme istovremeno da bude prijavljeno">Mesta</th>
              <th style={th}>Važi od</th><th style={th}>Važi do</th>
              <th style={{ ...th, textAlign: 'right' }} title="Dana do isteka; posle isteka ide 7 dana grejsa">Dana</th>
              <th style={th}>Paket</th>
              <th style={{ ...th, textAlign: 'right' }}>Akcije</th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', padding: 30, color: 'var(--color-text-muted)' }}>Nema licenci</td></tr>}
              {filtered.map((l) => {
                const status = STATUS_LABELS[l.status];
                const daysLeft = daysBetween(new Date().toISOString(), l.validUntil);
                return (
                  <tr key={l.id}>
                    <td style={{ ...td, fontFamily: 'monospace', fontWeight: 600 }}>
                      {l.moduleCode}
                      {l.isTrial && <span style={trialBadge}>TRIAL</span>}
                      {l.moduleCode === 'LPFR' && (
                        <div style={{ fontFamily: 'system-ui, sans-serif', fontSize: 11, fontWeight: 400, color: 'var(--color-text-muted)' }}>L-PFR (lokalni fiskalni uređaj) · mesta = uređaji</div>
                      )}
                    </td>
                    <td style={td}><span style={{ color: status.color, fontWeight: 600 }}>{status.label}</span></td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.seatsAllowed}</td>
                    <td style={{ ...td, fontSize: 11, color: 'var(--color-text-muted)' }}>{new Date(l.validFrom).toLocaleDateString('sr-Latn-RS')}</td>
                    <td style={{ ...td, fontSize: 11, color: 'var(--color-text-muted)' }}>{new Date(l.validUntil).toLocaleDateString('sr-Latn-RS')}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: daysLeft <= 7 ? '#fca5a5' : daysLeft <= 30 ? '#f59e0b' : 'var(--color-text-muted)', fontWeight: daysLeft <= 30 ? 600 : 400 }}>
                      {daysLeft >= 0 ? `${daysLeft}d` : `${Math.abs(daysLeft)}d istek`}
                    </td>
                    <td style={{ ...td, fontSize: 11, color: 'var(--color-text-muted)' }}>{l.packageCode || '-'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {l.status !== 'replaced' && (
                        <>
                          <button onClick={() => setRenewing(l)} style={iconBtn} title="Obnovi (produži rok)"><RotateCw size={13} /></button>
                          {l.status === 'active' && (
                            <button onClick={() => setSuspendTarget(l)} style={{ ...iconBtn, color: '#f59e0b' }} title="Suspenduj"><Pause size={13} /></button>
                          )}
                          <button onClick={() => setDeleteTarget(l)} style={{ ...iconBtn, color: '#fca5a5' }} title="Obriši"><Trash2 size={13} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {creatingFromPackage && (
        <PackageApplyDialog tenantId={tenantId} packages={packages} onClose={() => setCreatingFromPackage(false)} onSaved={() => { setCreatingFromPackage(false); refresh(); }} />
      )}
      {creatingIndividual && (
        <IndividualLicenseDialog tenantId={tenantId} modules={modules} onClose={() => setCreatingIndividual(false)} onSaved={() => { setCreatingIndividual(false); refresh(); }} />
      )}
      {renewing && (
        <RenewDialog tenantId={tenantId} license={renewing} onClose={() => setRenewing(null)} onSaved={() => { setRenewing(null); refresh(); }} />
      )}
      {suspendTarget && (
        <ConfirmDialog
          title="Suspenduj licencu"
          message={`Da li želite da suspendujete licencu za modul ${suspendTarget.moduleCode}? Korisnici tog tenant-a neće moći da koriste ovaj modul.`}
          confirmLabel="Suspenduj"
          variant="danger"
          onConfirm={confirmSuspend}
          onClose={() => setSuspendTarget(null)}
        />
      )}
      {deleteTarget && (
        <ConfirmDialog
          title="Obriši licencu"
          message={`Trajno obrisati licencu za modul ${deleteTarget.moduleCode}? Ova akcija se ne može opozvati.`}
          confirmLabel="Obriši"
          variant="danger"
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function PackageApplyDialog({ tenantId, packages, onClose, onSaved }: { tenantId: string; packages: LicensePackage[]; onClose: () => void; onSaved: () => void }) {
  useEscapeToClose(onClose);
  const [packageCode, setPackageCode] = useState(packages.find((p) => !p.isTrial)?.code || '');
  const [seatsAllowed, setSeatsAllowed] = useState<number | ''>('');
  const [validityDays, setValidityDays] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedPkg = packages.find((p) => p.code === packageCode);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body: any = { packageCode };
      if (seatsAllowed !== '') body.seatsAllowed = Number(seatsAllowed);
      if (validityDays !== '') body.validityDays = Number(validityDays);
      await api.post(`/admin/tenants/${tenantId}/licenses/from-package`, body);
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Primeni paket</h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Paket *">
            <select value={packageCode} onChange={(e) => setPackageCode(e.target.value)} required style={input}>
              <option value="">- Izaberi -</option>
              {packages.map((p) => <option key={p.code} value={p.code}>{p.name} ({p.modules.length} mod., {p.defaultSeats} mesta, {p.defaultValidityDays} d)</option>)}
            </select>
          </Field>
          {selectedPkg && (
            <div style={{ padding: 10, background: 'var(--color-bg)', borderRadius: 6, fontSize: 11, color: 'var(--color-text-muted)' }}>
              Moduli: {selectedPkg.modules.join(', ')}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Mesta (opciono - menja podrazumevano iz paketa)"><input type="number" value={seatsAllowed} onChange={(e) => setSeatsAllowed(e.target.value === '' ? '' : Number(e.target.value))} placeholder={String(selectedPkg?.defaultSeats || '')} style={input} /></Field>
            <Field label="Trajanje (dani, opciono)"><input type="number" value={validityDays} onChange={(e) => setValidityDays(e.target.value === '' ? '' : Number(e.target.value))} placeholder={String(selectedPkg?.defaultValidityDays || '')} style={input} /></Field>
          </div>
          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>{saving ? 'Čuvanje...' : 'Sačuvaj'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function IndividualLicenseDialog({ tenantId, modules, onClose, onSaved }: { tenantId: string; modules: LicenseModule[]; onClose: () => void; onSaved: () => void }) {
  useEscapeToClose(onClose);
  const [moduleCode, setModuleCode] = useState<ModuleCode | ''>('');
  const [seatsAllowed, setSeatsAllowed] = useState(1);
  const [validUntil, setValidUntil] = useState('');
  const [isTrial, setIsTrial] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Kad korisnik cekira "Trial", datum se automatski postavlja na +30 dana od danas
  // i polje postaje disabled. Kad otkaci, polje se cisti pa korisnik bira svoj datum.
  // Razlog: isTrial je inace samo label za badge prikaz - bez ovoga checkbox nema
  // funkcionalni efekat.
  useEffect(() => {
    if (isTrial) {
      const plus30 = new Date();
      plus30.setDate(plus30.getDate() + 30);
      setValidUntil(plus30.toISOString().slice(0, 10));
    } else {
      // Samo cisti ako je auto-set, ne da brisem sto je user uneo rucno
      // (ne pratimo da li je rucno, pa cisti). Korisnik zna da mora uneti datum.
      setValidUntil('');
    }
  }, [isTrial]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!moduleCode || !validUntil) { setError('Modul i datum su obavezni'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/admin/tenants/${tenantId}/licenses`, {
        moduleCode, seatsAllowed, validUntil: new Date(validUntil).toISOString(), isTrial,
      });
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Pojedinačna licenca</h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Modul *">
            <select value={moduleCode} onChange={(e) => setModuleCode(e.target.value as ModuleCode)} required style={input}>
              <option value="">- Izaberi modul -</option>
              {modules.map((m) => <option key={m.code} value={m.code}>{m.name}</option>)}
            </select>
          </Field>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 12, background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={isTrial} onChange={(e) => setIsTrial(e.target.checked)} style={{ marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>Trial licenca (30 dana)</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>
                {isTrial
                  ? 'Datum isteka je automatski postavljen 30 dana od danas. Licenca se prikazuje kao "trial" u listi. Obnavljanje kreira plaćenu licencu.'
                  : 'Plaćena licenca - unosite sopstveni datum isteka.'}
              </div>
            </div>
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label={moduleCode === 'LPFR' ? 'Broj uređaja' : 'Mesta'}><input type="number" min={1} value={seatsAllowed} onChange={(e) => setSeatsAllowed(Number(e.target.value))} required style={input} /></Field>
            <Field label={isTrial ? 'Važi do (auto)' : 'Važi do *'}>
              {isTrial ? (
                <input
                  type="text"
                  value={isoToDmy(validUntil)}
                  disabled
                  style={{ ...input, opacity: 0.7, cursor: 'not-allowed' }}
                  title="Trial je fiksiran na 30 dana; otkači checkbox za ručni datum"
                />
              ) : (
                <DmyDateInput value={validUntil} onChange={setValidUntil} required />
              )}
            </Field>
          </div>
          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>{saving ? 'Čuvanje...' : 'Sačuvaj'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RenewDialog({ tenantId, license, onClose, onSaved }: { tenantId: string; license: TenantLicense; onClose: () => void; onSaved: () => void }) {
  useEscapeToClose(onClose);
  const [validUntil, setValidUntil] = useState(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
  const [seatsAllowed, setSeatsAllowed] = useState(license.seatsAllowed);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post(`/admin/tenants/${tenantId}/licenses/${license.id}/renew`, {
        validUntil: new Date(validUntil).toISOString(),
        seatsAllowed,
      });
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Obnovi {license.moduleCode}</h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ padding: 10, background: 'var(--color-bg)', borderRadius: 6, fontSize: 12, color: 'var(--color-text-muted)' }}>
            Trenutna licenca ističe <strong>{new Date(license.validUntil).toLocaleDateString('sr-Latn-RS')}</strong>. Obnova kreira novu licencu sa početkom = trenutni kraj.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Novi datum isteka *"><DmyDateInput value={validUntil} onChange={setValidUntil} required /></Field>
            <Field label={license.moduleCode === 'LPFR' ? 'Broj uređaja' : 'Mesta'}><input type="number" min={1} value={seatsAllowed} onChange={(e) => setSeatsAllowed(Number(e.target.value))} style={input} /></Field>
          </div>
          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>{saving ? 'Čuvanje...' : 'Sačuvaj'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>{label}</label>{children}</div>;
}

/**
 * DmyDateInput - text input koji prihvata datum u dd/mm/yyyy formatu.
 *
 * Zasto ne `<input type="date">`: native date picker prati browser locale, pa
 * en-US masine pokazu mm/dd/yyyy a mi zelimo dd/mm/yyyy za sve korisnike.
 *
 * `value` i `onChange` rade sa ISO stringom (YYYY-MM-DD) - interno stanje je
 * string koji korisnik otkucava, ali se normalizuje u ISO na blur/submit.
 * Ako je text nevalidan, `onChange('')` se ne poziva - korisnik vidi originalni
 * text dok ga ne ispravi. Validacija kroz `pattern` + JS parsing.
 */
function DmyDateInput({
  value,
  onChange,
  required,
  autoFocus,
}: {
  value: string;
  onChange: (isoDate: string) => void;
  required?: boolean;
  autoFocus?: boolean;
}) {
  // value je ISO YYYY-MM-DD; display je dd/mm/yyyy
  const [text, setText] = useState(() => isoToDmy(value));

  // Kad value promena spolja (npr. reset), sync-uj text
  useEffect(() => {
    setText(isoToDmy(value));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setText(raw);
    // Pokusaj parsiranja; ako je kompletan dd/mm/yyyy i validan, propagiraj ISO
    const iso = dmyToIso(raw);
    if (iso) onChange(iso);
  };

  const handleBlur = () => {
    // Na blur normalizuj prikaz - ako je validan, prikazi formatiran tekst
    const iso = dmyToIso(text);
    if (iso) {
      setText(isoToDmy(iso));
    }
  };

  return (
    <input
      type="text"
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder="dd/mm/yyyy"
      inputMode="numeric"
      pattern="\d{2}/\d{2}/\d{4}"
      title="Format: dd/mm/yyyy (npr. 31/12/2026)"
      required={required}
      autoFocus={autoFocus}
      style={input}
    />
  );
}

function isoToDmy(iso: string): string {
  if (!iso) return '';
  // iso je YYYY-MM-DD ili puni ISO timestamp
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return '';
  return `${match[3]}/${match[2]}/${match[1]}`;
}

function dmyToIso(dmy: string): string | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dmy.trim());
  if (!match) return null;
  const [, dd, mm, yyyy] = match;
  const day = parseInt(dd, 10);
  const month = parseInt(mm, 10);
  const year = parseInt(yyyy, 10);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  if (year < 1900 || year > 2100) return null;
  // Validate actual calendar (hvata 31/02/2026 itd.)
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return null;
  return `${yyyy}-${mm}-${dd}`;
}

const card: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' };
const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '12px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const trialBadge: React.CSSProperties = { marginLeft: 6, fontSize: 9, padding: '1px 6px', borderRadius: 8, background: 'rgba(245,158,11,0.2)', color: '#fbbf24', fontWeight: 700 };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 560, background: 'var(--color-bg-surface)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--color-border)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-strong)' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const input: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, outline: 'none' };
